// useTableState.ts
import { useCallback, useMemo, useState } from 'react';
import type {
  ColumnFiltersState,
  OnChangeFn,
  SortingState,
} from '@tanstack/react-table';
import { PaginationProps } from 'antd';

export function useDashBoardSettingsPagination() {
  const [sortingState, setSortingState] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [totalElements, setTotalElements] = useState<number>(0);

  const handleTableFilter: OnChangeFn<ColumnFiltersState> = useCallback(
    (updaterOrValue) => {
      setColumnFilters((current) =>
        updaterOrValue instanceof Function
          ? updaterOrValue(current)
          : updaterOrValue,
      );
    },
    [],
  );

  const queryParameters = useMemo(
    () => ({
      page: pageNumber,
      sort: sortingState
        .map((sort) => `${sort.id},${sort.desc ? 'desc' : 'asc'}`)
        .join(','),
      ...columnFilters.reduce(
        (acc, filter) => {
          acc[filter.id] = filter.value;
          return acc;
        },
        {} as Record<string, any>,
      ),
    }),
    [columnFilters, pageNumber, sortingState],
  );

  const tableProps = useMemo(
    () => ({
      filters: columnFilters,
      onFilter: handleTableFilter,
      sort: sortingState,
      onSort: setSortingState,
    }),
    [columnFilters, handleTableFilter, sortingState],
  );

  const paginationProps: PaginationProps = useMemo(
    () => ({
      current: pageNumber + 1,
      total: totalElements,
      onChange: (pageNumber) => {
        setPageNumber(pageNumber - 1);
      },
      showSizeChanger: true,
    }),
    [totalElements, pageNumber],
  );

  return {
    queryParameters,
    tableProps,
    paginationProps,
    setTotal: setTotalElements,
  };
}
